import React, { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import {
    Tv,
    Theater,
    Smile,
    Sparkles,
    Layers,
    User,
    Sliders,
    FolderHeart,
    UserCheck,
    Image as ImageIcon,
    Zap
} from 'lucide-react';

const actingServices = [
    { id: '01', icon: Theater, title: 'Theatrical Headshot Masters', desc: 'Capturing raw, textured, and deeply expressive portraits designed to showcase your dramatic depth for film, television, and stage casting.' },
    { id: '02', icon: Smile, title: 'Commercial & Print Expressions', desc: 'Bright, high-energy, approachable captures delivering the vibrant personality traits required by mainstream commercial brands.' },
    { id: '03', icon: Sparkles, title: 'Character Typing Portfolios', desc: 'Custom wardrobe and lighting configurations sculpted to explicitly target your specific casting brackets—from the gritty detective to the elite executive.' },
    { id: '04', icon: Tv, title: 'On-Camera Slates & Screen Caps', desc: 'Clean, cinematic framing matching the visual texture of modern streaming networks to instantly look native on a casting submission.' },
    { id: '05', icon: User, title: 'Physical Theatre & Movement Stills', desc: 'Dynamic, unposed action capture highlighting physical range, body alignment, and spatial expression for dancers and stage performers.' },
    { id: '06', icon: Layers, title: 'Indie Production Promo Stills', desc: 'High-concept, moody promotional imagery crafted to give independent film projects a striking key visual for press releases and festival packages.' },
];

const actingDeliverables = [
    { id: '01', icon: UserCheck, title: 'Casting-Director Approved Framing', desc: 'Strict adherence to international headshot standards, focusing directly on clean chest-up alignment and absolute eye clarity.' },
    { id: '02', icon: Sliders, title: 'Authentic Expression Prompting', desc: 'Ditching clinical poses for interactive psychological triggers that draw out real thought processes and intense presence.' },
    { id: '03', icon: Theater, title: 'Theatrical vs. Commercial Separations', desc: 'Dedicated lighting adjustments instantly shifting from cinematic moody shadows to open, flattering commercial brilliance.' },
    { id: '04', icon: Zap, title: 'Catchlight Eye Architecture', desc: 'Precision placement of specialized studio key-lights calculated to fill the irises with vibrant life and undeniable casting pull.' },
    { id: '05', icon: User, title: 'Wardrobe Cohesion Consulting', desc: 'On-set editing of clothing textures and necklines to perfectly frame the neck and face without distracting from your micro-expressions.' },
    { id: '06', icon: Tv, title: 'Natural Background Desaturation', desc: 'Utilizing massive focal lengths to completely isolate you against creamy, cinematic backdrops that keep the focus 100% on you.' },
    { id: '07', icon: Layers, title: 'Multi-Look Palette Management', desc: 'A meticulously timed session schedule enabling effortless transitions through multiple hair variations and clothing layers.' },
    { id: '08', icon: FolderHeart, title: 'Representational Collaboration Hubs', desc: 'Interactive online download galleries built to let you, your acting coach, and your talent agent easily star, sort, and comment.' },
    { id: '09', icon: ImageIcon, title: 'Permanent Master File Archives', desc: 'Securing your ultimate high-resolution uncompressed master assets across redundant vaults for lifetime print re-orders.' },
];

const ActingPhotographyPortfolio = () => {
    const viewportRef = useRef(null);
    const leftStripRef = useRef(null);
    const rightStripRef = useRef(null);

    useEffect(() => {
        const viewport = viewportRef.current;
        const leftStrip = leftStripRef.current;
        const rightStrip = rightStripRef.current;

        if (!viewport || !leftStrip || !rightStrip) return;

        let currentPosLeft = 0;
        let currentPosRight = 0;
        let isHovered = false;
        let animationFrameId;

        const speedLeft = 0.58;
        const speedRight = 0.38;

        const handleMouseEnter = () => { isHovered = true; };
        const handleMouseLeave = () => { isHovered = false; };

        viewport.addEventListener('mouseenter', handleMouseEnter);
        viewport.addEventListener('mouseleave', handleMouseLeave);

        function performAutoScroll() {
            if (!isHovered && leftStrip && rightStrip) {
                currentPosLeft -= speedLeft;
                currentPosRight -= speedRight;

                if (Math.abs(currentPosLeft) >= (leftStrip.scrollHeight / 2)) {
                    currentPosLeft = 0;
                }
                if (Math.abs(currentPosRight) >= (rightStrip.scrollHeight / 2)) {
                    currentPosRight = 0;
                }

                if (window.innerWidth > 991) {
                    leftStrip.style.transform = `translateY(${currentPosLeft}px)`;
                    rightStrip.style.transform = `translateY(${currentPosRight}px)`;
                } else {
                    leftStrip.style.transform = 'none';
                    rightStrip.style.transform = 'none';
                }
            }
            animationFrameId = requestAnimationFrame(performAutoScroll);
        }

        animationFrameId = requestAnimationFrame(performAutoScroll);

        return () => {
            cancelAnimationFrame(animationFrameId);
            if (viewport) {
                viewport.removeEventListener('mouseenter', handleMouseEnter);
                viewport.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, []);

    return (
        <div>
            <Helmet>
                <title>Acting Photography Portfolio in Hyderabad | Actor & Actress Portfolio Shoots | KhultStudios</title>

                <meta
                    name="description"
                    content="Professional acting portfolio photography in Hyderabad by KhultStudios. Get industry-standard actor headshots, actress portfolios, casting photos, audition profiles, cinematic portraits, and personal branding shoots."
                />

                <meta
                    name="keywords"
                    content="Acting Portfolio Hyderabad, Actor Portfolio Photography Hyderabad, Actress Portfolio Photography, Acting Photography Hyderabad, Actor Headshots Hyderabad, Casting Portfolio Photography, Audition Photography, Film Industry Portfolio, Tollywood Actor Portfolio, Professional Headshots Hyderabad, Cinematic Portrait Photography, Model and Actor Portfolio, Personal Branding Photography, KhultStudios"
                />

                <meta name="robots" content="index, follow" />
                <meta name="author" content="KhultStudios" />

                <link
                    rel="canonical"
                    href="https://khultstudios.com/acting-photography-portfolio"
                />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta
                    property="og:url"
                    content="https://khultstudios.com/acting-photography-portfolio"
                />
                <meta
                    property="og:title"
                    content="Acting Photography Portfolio in Hyderabad | KhultStudios"
                />
                <meta
                    property="og:description"
                    content="Build a professional acting portfolio with industry-standard headshots, casting photos, audition portraits, and cinematic actor photography in Hyderabad."
                />
                <meta
                    property="og:image"
                    content="https://khultstudios.com/images/acting-portfolio-banner.jpg"
                />
                <meta property="og:site_name" content="KhultStudios" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content="Acting Photography Portfolio | KhultStudios"
                />
                <meta
                    name="twitter:description"
                    content="Professional actor and actress portfolio photography, casting headshots, and audition-ready portraits in Hyderabad."
                />
                <meta
                    name="twitter:image"
                    content="https://khultstudios.com/images/acting-portfolio-banner.jpg"
                />

                {/* Favicon */}
                <link rel="icon" href="https://khultstudios.com/favicon.ico" />
            </Helmet>
            {/* FULLSCREEN VIDEO VIEWPORT BACKGROUND ENGINE */}
            <div className="video-bg-viewport">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="global-html5-video"
                >
                    <source src="videos/actor_reel_stills.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                </video>
                <div className="vignette-overlay-layer" />
            </div>

            {/* INNER APP CANVAS CONTAINER SECTION */}
            <div className="page-frame px-2 px-md-5 pb-5">
                <div className="landing-container position-relative overflow-hidden">
                    <div className="paper-texture" />
                    <div className="container-fluid h-100 p-4 p-lg-5 position-relative z-2">
                        <div className="row h-100 gy-5">

                            {/* Content Brief side description */}
                            <div className="col-lg-5 d-flex flex-column justify-content-center h-100 content-side">
                                <div className="invitation-text position-relative py-4">
                                    <h5 className="text-uppercase tracking-wider text-orange fw-bold mb-2">
                                        Professional Actor Headshots & Portfolios
                                    </h5>
                                    <h1 className="display-3 text-uppercase main-title text-white mb-4">
                                        Headshots That Command The Room
                                    </h1>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        A casting director spends less than two seconds looking at your thumbnail. If your headshot looks like a static school portrait or doesn't radiate absolute human presence, your submission disappears. Your visual calling card needs to lock eyes and tell a story instantly.
                                    </p>
                                    <p className="lead-text text-light-gray mb-4 col-xl-10">
                                        At KhultStudio, we specialize in high-impact actor portfolio design. By blending character-typing frameworks, vivid catchlight eye architecture, and zero skin-altering over-processing, we craft images that look precisely like *you* on your absolute best day.
                                    </p>
                                    <a
                                        href="#details"
                                        className="btn btn-orange text-uppercase px-4 py-2 text-dark fw-bold tracking-wide btn-scroll"
                                    >
                                        Explore Sessions
                                    </a>
                                </div>
                                <div className="social-footer d-flex gap-3 mt-2">
                                    <a href="#" className="social-icon"><i className="fab fa-facebook-f" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-instagram" /></a>
                                    <a href="#" className="social-icon"><i className="fab fa-pinterest" /></a>
                                </div>
                            </div>

                            {/* Infinite Slanted Ribbon Gallery Strip components */}
                            <div className="col-lg-7 position-relative photo-zone-height">
                                <div className="filmstrip-skew-box">
                                    <div
                                        className="vertical-scroll-viewport"
                                        ref={viewportRef}
                                    >
                                        <div className="row gx-4 h-100">
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon" ref={leftStripRef} style={{ display: 'none' }}>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=500" alt="Dramatic Theatrical Actress Headshot Portrait" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=500" alt="Commercial Friendly Male Actor Headshot" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=500" alt="Character Archetype Gritty Detective Typing Capture" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=500" alt="Intense Dramatic Theater Performance Portrait" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=500" alt="Dramatic Theatrical Actress Headshot Portrait Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=500" alt="Commercial Friendly Male Actor Headshot Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=500" alt="Character Archetype Gritty Detective Typing Capture Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=500" alt="Intense Dramatic Theater Performance Portrait Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="filmstrip-ribbon ribbon-offset" ref={rightStripRef}>
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography.jpg" alt="Wedding 5" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography1.jpg" alt="Wedding 6" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography2.jpg" alt="Wedding 7" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography3.jpg" alt="Wedding 8" />
                                                    </div>
                                                    {/* Clone Buffers */}
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography.jpg" alt="Wedding 5 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography1.jpg" alt="Wedding 6 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography2.jpg" alt="Wedding 7 Buffer" />
                                                    </div>
                                                    <div className="ribbon-cell">
                                                        <img src="/acting-photography/acting-photography3.jpg" alt="Wedding 8 Buffer" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </div>

            {/* PERFORMANCE INDUSTRY PORTFOLIO SERVICES SECTION */}
            <section className="services-section py-5" id="details">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">🎭 Industry-Standard Performance Portraiture</h2>
                    <p className="text-light-gray mt-3 text-center max-w-2xl mx-auto">
                        Casting requirements vary wildly across projects. Our tailored headshot modules are engineered around the structural expectations of elite casting networks, driving focus entirely to your emotional capability.
                    </p>
                    <div className="row g-4 mt-2">
                        {actingServices.map((service, index) => (
                            <div key={index} className="col-md-4">
                                <div className={`service-card ${index === 0 ? 'active' : ''}`}>
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h3>{service.title}</h3>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* HEADSHOT OPERATIONAL SPECIFICATION MATRIX */}
            <section className="comprehensive-section py-5 bg-black-opacity-50">
                <div className="container">
                    <h2 className="text-center mb-5 text-white">⚙️ Technical Execution & On-Set Supporting Frameworks</h2>
                    <div className="row g-4">
                        {actingDeliverables.map((service, index) => (
                            <div key={index} className="col-lg-4 col-md-6">
                                <div className="service-card">
                                    <div className="card-content">
                                        <service.icon className="icon mb-3" size={40} />
                                        <h4>{service.title}</h4>
                                        <p>{service.desc}</p>
                                    </div>
                                    <span className="card-number">{service.id}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ActingPhotographyPortfolio;